import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { analytics } from '../services/analytics';

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.medium};
  border: 1px solid ${props => props.theme.colors.text.accent};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 1rem;
  background-color: rgba(24, 24, 36, 0.7);
  color: ${props => props.theme.colors.text.primary};
  transition: all ${props => props.theme.transitions.medium};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) inset;

  &::placeholder {
    color: ${props => props.theme.colors.text.secondary};
    opacity: 0.7;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.text.accent};
    background-color: rgba(28, 28, 40, 0.9);
    box-shadow: 0 0 0 2px rgba(74, 107, 175, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1) inset;
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xsmall};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform ${props => props.theme.transitions.fast};
  
  &:hover {
    transform: translateY(-50%) scale(1.1);
  }
  
  & svg path {
    stroke: ${props => props.theme.colors.text.secondary};
    transition: stroke ${props => props.theme.transitions.fast};
  }
  
  &:hover svg path {
    stroke: ${props => props.theme.colors.text.accent};
  }
`;

const SearchBar: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const isOnSearchPage = location.pathname === '/search';

  // Extract search query from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q');
    if (query) {
      setSearchTerm(query);
    } else {
      // Limpiar la búsqueda si no hay query en la URL
      setSearchTerm('');
    }
  }, [location.search, location.pathname]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Track search event
      analytics.search(searchTerm.trim());
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  // Función para limpiar la búsqueda
  const handleClearSearch = () => {
    setSearchTerm('');
    // Si estamos en la página de búsqueda, volver a la página principal
    if (isOnSearchPage) {
      navigate('/');
    }
  };

  return (
    <SearchContainer>
      <form onSubmit={handleSubmit}>
        <SearchInput
          type="text"
          placeholder="Buscar imágenes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Buscar imágenes"
        />
        {searchTerm ? (
          // Mostrar botón de cancelar si hay texto
          <SearchButton 
            type="button" 
            onClick={handleClearSearch} 
            aria-label="Cancelar búsqueda"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor"/>
            </svg>
          </SearchButton>
        ) : (
          // Mostrar lupa si no hay texto
          <SearchButton type="submit" aria-label="Buscar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor"/>
            </svg>
          </SearchButton>
        )}
      </form>
    </SearchContainer>
  );
};

export default SearchBar;